import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check, ChevronsUpDown, Home, PawPrint, Plus, Trash2 } from 'lucide-react'

import { AdditionalServices } from '@/components/additional-services'
import { useClient } from '@/entities/client/model/useClient'
import { cn } from '@/shared/lib/styles/class-merge'
import { ROOMS } from '@/shared/types/rooms'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/command'
import { BufferedNumberInput } from '@/shared/ui/buffered-number-input'
import { BufferedTextInput } from '@/shared/ui/buffered-text-input'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Separator } from '@/shared/ui/separator'
import { Textarea } from '@/shared/ui/textarea'
import { type HotelReservation, type Pet, type PetSize } from '@monorepo/functions/src/types/reservations'
import { type AdditionalService } from '@monorepo/functions/src/types/services'

interface PetsAndServicesCardProps {
    reservation: HotelReservation
    onPetsChange: (pets: Pet[]) => void
    handleServiceChange: (services: AdditionalService[]) => void
    clientId: string
}

export function PetsAndServicesCard({
    reservation,
    onPetsChange,
    handleServiceChange,
    clientId,
}: PetsAndServicesCardProps) {
    const { t } = useTranslation()
    const { client, setClientSync: setClient } = useClient(clientId)
    const [openRoomSelectors, setOpenRoomSelectors] = useState<{ [key: number]: boolean }>({})

    // Merge profile from client only when names match (non-empty). Avoid matching every pet with "".
    const getMergedPetData = (reservationPet: Pet): Pet => {
        if (!client) return reservationPet

        const name = reservationPet.name?.trim() ?? ''
        if (!name) return reservationPet

        const clientPet = client.pets.find(pet => pet.name === reservationPet.name)
        if (clientPet) {
            return {
                ...clientPet,
                roomNumber: reservationPet.roomNumber,
            }
        }

        return reservationPet
    }

    const updatePetField = async (petIndex: number, field: keyof Pet, value: any) => {
        const currentPet = reservation.pets[petIndex]
        const newPets = [...reservation.pets]
        const updatedPet = { ...currentPet, [field]: value }
        newPets[petIndex] = updatedPet
        onPetsChange(newPets)

        // Keep clients/{id}.pets in sync (used in «Mascotas Asociadas»). Match by id, then name, then slot index.
        if (!client) return

        const updatedClientPets = [...client.pets]
        let clientPetIndex = -1

        if (currentPet.id?.trim()) {
            clientPetIndex = updatedClientPets.findIndex(p => p.id === currentPet.id)
        }
        if (clientPetIndex === -1 && currentPet.name?.trim()) {
            clientPetIndex = updatedClientPets.findIndex(pet => pet.name === currentPet.name)
        }
        const sameSlotCount = reservation.pets.length === client.pets.length
        if (clientPetIndex === -1 && sameSlotCount && petIndex < updatedClientPets.length) {
            clientPetIndex = petIndex
        }

        if (clientPetIndex !== -1) {
            updatedClientPets[clientPetIndex] = { ...updatedClientPets[clientPetIndex], [field]: value }
            await setClient({ ...client, pets: updatedClientPets })
        }
    }

    const toggleRoomSelector = (petIndex: number) => {
        setOpenRoomSelectors(prev => ({
            ...prev,
            [petIndex]: !prev[petIndex],
        }))
    }

    const renderPets = () => {
        return (
            <div className='space-y-6'>
                {reservation.pets.map((reservationPet, petIndex) => {
                    const pet = getMergedPetData(reservationPet)
                    return (
                        <Card key={pet.id || petIndex} className='overflow-hidden border border-gray-200 shadow-sm'>
                            <CardHeader className='flex flex-row items-center justify-between bg-gray-50 py-3'>
                                <CardTitle className='text-base font-medium'>
                                    Mascota {petIndex + 1}
                                    {pet.name && <span className='ml-1 text-primary'>- {pet.name}</span>}
                                </CardTitle>
                                <Button
                                    variant='destructive'
                                    size='sm'
                                    onClick={() => {
                                        const newPets = reservation.pets.filter((_, i) => i !== petIndex)
                                        onPetsChange(newPets)
                                    }}
                                >
                                    <Trash2 className='mr-1 h-4 w-4' />
                                    Eliminar
                                </Button>
                            </CardHeader>
                            <CardContent className='p-4'>
                                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                                    <div>
                                        <Label htmlFor={`pet-name-${petIndex}`} className='text-sm font-medium'>
                                            Nombre
                                        </Label>
                                        <BufferedTextInput
                                            id={`pet-name-${petIndex}`}
                                            value={pet.name}
                                            onDebouncedChange={value => updatePetField(petIndex, 'name', value)}
                                            className='mt-1'
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-breed-${petIndex}`} className='text-sm font-medium'>
                                            Raza
                                        </Label>
                                        <BufferedTextInput
                                            id={`pet-breed-${petIndex}`}
                                            value={pet.breed}
                                            onDebouncedChange={value => updatePetField(petIndex, 'breed', value)}
                                            className='mt-1'
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-weight-${petIndex}`} className='text-sm font-medium'>
                                            Peso (kg)
                                        </Label>
                                        <BufferedNumberInput
                                            key={`pet-weight-${petIndex}`}
                                            id={`pet-weight-${petIndex}`}
                                            step='0.1'
                                            initialValue={pet.weight ?? ''}
                                            onDebouncedChange={weight => updatePetField(petIndex, 'weight', weight)}
                                            className='mt-1'
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-age-${petIndex}`} className='text-sm font-medium'>
                                            {t('pet.age', 'Edad')}
                                        </Label>
                                        <BufferedTextInput
                                            id={`pet-age-${petIndex}`}
                                            value={pet.age ?? ''}
                                            onDebouncedChange={value => updatePetField(petIndex, 'age', value)}
                                            className='mt-1'
                                            placeholder={t('pet.agePlaceholder', 'Ej: 2 años, 6 meses, 18 meses')}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-size-${petIndex}`} className='text-sm font-medium'>
                                            Tamaño
                                        </Label>
                                        <Select
                                            value={pet.size}
                                            onValueChange={(value: PetSize) => updatePetField(petIndex, 'size', value)}
                                        >
                                            <SelectTrigger id={`pet-size-${petIndex}`} className='mt-1'>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='pequeño'>Pequeño</SelectItem>
                                                <SelectItem value='mediano'>Mediano</SelectItem>
                                                <SelectItem value='grande'>Grande</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-sex-${petIndex}`} className='text-sm font-medium'>
                                            Sexo
                                        </Label>
                                        <Select
                                            value={pet.sex}
                                            onValueChange={(value: 'M' | 'F') => updatePetField(petIndex, 'sex', value)}
                                        >
                                            <SelectTrigger id={`pet-sex-${petIndex}`} className='mt-1'>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='M'>Macho</SelectItem>
                                                <SelectItem value='F'>Hembra</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-room-${petIndex}`} className='text-sm font-medium'>
                                            Habitación
                                        </Label>
                                        <Popover
                                            open={openRoomSelectors[petIndex] || false}
                                            onOpenChange={() => toggleRoomSelector(petIndex)}
                                        >
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant='outline'
                                                    role='combobox'
                                                    aria-expanded={openRoomSelectors[petIndex] || false}
                                                    className={cn(
                                                        'mt-1 min-h-[2.75rem] w-full justify-between',
                                                        !pet.roomNumber
                                                            ? 'border-amber-300 bg-amber-100 hover:bg-amber-200'
                                                            : '',
                                                    )}
                                                >
                                                    <div className='flex w-full items-center gap-2'>
                                                        <Home
                                                            className={`h-4 w-4 shrink-0 ${!pet.roomNumber ? 'text-amber-500' : ''}`}
                                                        />
                                                        {pet.roomNumber ? (
                                                            <div className='flex min-w-0 flex-col gap-0.5 pl-2'>
                                                                <div className='truncate text-start font-medium'>
                                                                    {pet.roomNumber}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className='text-amber-700'>Asignar Habitación</span>
                                                        )}
                                                    </div>
                                                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className='w-[--radix-popover-trigger-width] p-0'>
                                                <Command>
                                                    <CommandInput placeholder='Buscar habitación...' />
                                                    <CommandEmpty>No se encontraron habitaciones.</CommandEmpty>
                                                    <CommandList className='max-h-[300px] overflow-auto'>
                                                        {ROOMS.map(room => (
                                                            <CommandItem
                                                                key={room}
                                                                value={room}
                                                                onSelect={() => {
                                                                    updatePetField(petIndex, 'roomNumber', room)
                                                                    toggleRoomSelector(petIndex)
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        'mr-2 h-4 w-4',
                                                                        pet.roomNumber === room
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )}
                                                                />
                                                                <div className='flex min-w-0 flex-col gap-0.5 pl-2'>
                                                                    <div className='truncate text-start font-medium'>
                                                                        {room}
                                                                    </div>
                                                                </div>
                                                            </CommandItem>
                                                        ))}
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div>
                                        <Label htmlFor={`pet-neutered-${petIndex}`} className='text-sm font-medium'>
                                            Castrado/Esterilizado
                                        </Label>
                                        <Select
                                            value={pet.isNeutered ? 'yes' : 'no'}
                                            onValueChange={(value: 'yes' | 'no') =>
                                                updatePetField(petIndex, 'isNeutered', value === 'yes')
                                            }
                                        >
                                            <SelectTrigger id={`pet-neutered-${petIndex}`} className='mt-1'>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value='yes'>Sí</SelectItem>
                                                <SelectItem value='no'>No</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className='sm:col-span-2'>
                                        <Label htmlFor={`pet-personality-${petIndex}`} className='text-sm font-medium'>
                                            Personalidad
                                        </Label>
                                        <Textarea
                                            id={`pet-personality-${petIndex}`}
                                            value={pet.personality || ''}
                                            onChange={e => updatePetField(petIndex, 'personality', e.target.value)}
                                            className='mt-1'
                                            placeholder='Tranquilo, nervioso, sociable...'
                                        />
                                    </div>
                                    <div className='sm:col-span-2'>
                                        <Label htmlFor={`pet-observations-${petIndex}`} className='text-sm font-medium'>
                                            {t('pet.observations', 'Observaciones')}
                                        </Label>
                                        <Textarea
                                            id={`pet-observations-${petIndex}`}
                                            value={pet.observations || ''}
                                            onChange={e => updatePetField(petIndex, 'observations', e.target.value)}
                                            className='mt-1'
                                            placeholder={t(
                                                'pet.observationsPlaceholder',
                                                'Observaciones especiales sobre la mascota...',
                                            )}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )
                })}

                <div className='flex justify-center'>
                    <Button
                        variant='outline'
                        onClick={() => {
                            const newPet: Pet = {
                                name: '',
                                breed: '',
                                age: '',
                                weight: 0,
                                size: 'pequeño',
                                sex: 'M',
                                isNeutered: false,
                                personality: '',
                            }
                            onPetsChange([...reservation.pets, newPet])
                        }}
                    >
                        <Plus className='mr-2 h-4 w-4' />
                        Añadir Mascota
                    </Button>
                </div>
            </div>
        )
    }

    const renderServices = () => {
        return (
            <div className='space-y-4'>
                <AdditionalServices
                    onServiceChange={handleServiceChange}
                    initialServices={reservation.additionalServices}
                    petCount={reservation.pets.length}
                    petNames={reservation.pets.map(pet => pet.name)}
                />
            </div>
        )
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className='flex items-center gap-2 text-base font-medium'>
                    <PawPrint className='h-4 w-4' />
                    Mascotas y Servicios
                </CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
                {renderPets()}
                <Separator />
                {renderServices()}
            </CardContent>
        </Card>
    )
}
